import { useSettings } from "@/context/SettingsContext";
import { fileUrl } from "@/lib/api";
import { Instagram, Mail, MapPin, Phone, Facebook } from "lucide-react";

export default function Footer() {
  const { settings } = useSettings();
  const logo = settings?.logo_path ? fileUrl(settings.logo_path) : null;
  const brandName = settings?.legal_business_name || "Maison Pilet";

  return (
    <footer className="bg-linen border-t border-taupe/40 mt-32" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-20 grid md:grid-cols-4 gap-12">
        {/* Brand block */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-5 mb-6">
            {logo ? (
              <div className="relative shrink-0">
                <span className="absolute -top-1 -left-1 w-3 h-3 border-t border-l border-brass" aria-hidden />
                <span className="absolute -top-1 -right-1 w-3 h-3 border-t border-r border-brass" aria-hidden />
                <span className="absolute -bottom-1 -left-1 w-3 h-3 border-b border-l border-brass" aria-hidden />
                <span className="absolute -bottom-1 -right-1 w-3 h-3 border-b border-r border-brass" aria-hidden />
                <img src={logo} alt={brandName} className="h-20 w-20 object-contain bg-ivory p-2" data-testid="footer-logo" />
              </div>
            ) : (
              <img src="/maison-pilet-logo.png" alt={brandName} className="h-20 w-16 object-contain shrink-0" data-testid="footer-logo" />
            )}
            <div>
              <h3 className="font-serif text-3xl lowercase text-charcoal leading-none">{brandName.toLowerCase()}</h3>
              <p className="editorial-label mt-2">{(settings?.tagline || "timeless over trendy.").toLowerCase()}</p>
            </div>
          </div>
          <p className="text-sm text-espresso leading-relaxed max-w-md">
            an editorial resale boutique — pre-loved garments restored with intention,
            photographed in studio, and offered as quiet luxury for everyday living.
          </p>
        </div>

        {/* Contact */}
        <div>
          <p className="editorial-label mb-6">house · contact</p>
          <ul className="space-y-3 text-sm text-charcoal">
            {settings?.support_email && (
              <li className="flex items-center gap-3" data-testid="footer-email">
                <Mail className="w-4 h-4 text-brass flex-shrink-0" />
                <a href={`mailto:${settings.support_email}`} className="hover:text-brass">{settings.support_email}</a>
              </li>
            )}
            {settings?.contact_phone && (
              <li className="flex items-center gap-3" data-testid="footer-phone">
                <Phone className="w-4 h-4 text-brass flex-shrink-0" />
                <span>{settings.contact_phone}</span>
              </li>
            )}
            {settings?.physical_address && (
              <li className="flex items-start gap-3" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" />
                <span className="leading-relaxed">{settings.physical_address}</span>
              </li>
            )}
          </ul>
        </div>

        {/* Social */}
        <div>
          <p className="editorial-label mb-6">follow · the journal</p>
          <ul className="space-y-3 text-sm text-charcoal">
            {settings?.instagram_handle && (
              <li className="flex items-center gap-3"><Instagram className="w-4 h-4 text-brass" />{settings.instagram_handle}</li>
            )}
            {settings?.facebook_handle && (
              <li className="flex items-center gap-3"><Facebook className="w-4 h-4 text-brass" />{settings.facebook_handle}</li>
            )}
            {settings?.pinterest_handle && (
              <li className="flex items-center gap-3"><span className="w-4 h-4 text-brass">P</span>{settings.pinterest_handle}</li>
            )}
          </ul>
          <p className="mt-12 editorial-label">quiet luxury for everyday living.</p>
        </div>
      </div>

      <div className="border-t border-taupe/40 py-6 text-center editorial-label flex items-center justify-center gap-4 flex-wrap">
        <span>© {new Date().getFullYear()} · {brandName.toLowerCase()} · all rights reserved</span>
        <span className="text-taupe">·</span>
        <a href="/privacy" className="hover:text-brass" data-testid="footer-privacy-link">privacy policy</a>
      </div>
    </footer>
  );
}
